import React, { useState, useEffect, useRef } from "react";
import { SEO } from "../../components/shared/SEO";
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  LinearProgress,
} from "@mui/material";
import {
  PlayArrow,
  Stop,
  FreeBreakfast,
  Download,
  AccessTime,
  WbSunny,
  InfoOutlined,
  CalendarToday,
  EventAvailable,
  Cancel,
  Home,
  CheckCircle,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { showSnackbar } from "../../store/slices/uiSlice";
import { attendanceActionAsync } from "../../store/slices/dataSlice";
import { ROLES_CONFIG } from "../../types";
import Avatar from "../../components/shared/Avatar";
import StatusBadge from "../../components/shared/StatusBadge";
import StatCard from "../../components/shared/StatCard";

type ClockStatus = "idle" | "working" | "break" | "done";
const fmtSec = (s: number) =>
  `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
const fmtHM = (s: number) =>
  `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
const fmtTime = (d: Date) =>
  d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

const attStats = [
  {
    label: "Present Today",
    value: "211",
    icon: "CheckCircle",
    delta: "85.1%",
    deltaType: "up" as const,
    color: "#16a34a",
    iconBg: "#dcfce7",
  },
  {
    label: "Absent",
    value: "18",
    icon: "Cancel",
    delta: "7.3%",
    deltaType: "down" as const,
    color: "#dc2626",
    iconBg: "#fee2e2",
  },
  {
    label: "On Leave",
    value: "11",
    icon: "EventAvailable",
    delta: "4.4%",
    deltaType: "neutral" as const,
    color: "#d97706",
    iconBg: "#fef3c7",
  },
  {
    label: "WFH",
    value: "8",
    icon: "Home",
    delta: "3.2%",
    deltaType: "neutral" as const,
    color: "#6BB8C3",
    iconBg: "#C9FDF2",
  },
];

// Using global theme for table headers

// Mock Data
const weeklyData = [
  { name: "MON", value: 8, color: "#f1f5f9" },
  { name: "TUE", value: 8.5, color: "#f1f5f9" },
  { name: "WED", value: 7.5, color: "#f1f5f9" },
  { name: "THU", value: 8, color: "#f1f5f9" },
  { name: "FRI", value: 4, color: "#e2e8f0" }, // Today placeholder color
  { name: "SAT", value: 0, color: "#f1f5f9" },
  { name: "SUN", value: 0, color: "#f1f5f9" },
];

const recentActivity = [
  {
    time: "YESTERDAY, 18:05",
    title: "Clocked Out from shift",
    color: "#B6F2D1",
  },
  {
    time: "YESTERDAY, 13:30",
    title: "Break Ended (45m duration)",
    color: "#d97706",
  },
  { time: "YESTERDAY, 12:45", title: "Break Started", color: "#d97706" },
  {
    time: "YESTERDAY, 08:58",
    title: "Clocked In from home office",
    color: "#B6F2D1",
  },
];

const getUpcomingHolidays = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Start of today
  const currentYear = today.getFullYear();

  const holidaysList = [
    {
      month: 0,
      date: 1,
      name: "New Year's Day",
      sub: "National Holiday",
      tag: "PAID",
    },
    {
      month: 4,
      date: 1,
      name: "Labor Day",
      sub: "National Holiday",
      tag: "PAID",
    },
    {
      month: 6,
      date: 4,
      name: "Independence Day",
      sub: "National Holiday",
      tag: "PAID",
    },
    { month: 9, date: 31, name: "Halloween", sub: "Regional Holiday", tag: "" },
    {
      month: 10,
      date: 11,
      name: "Veterans Day",
      sub: "National Holiday",
      tag: "PAID",
    },
    {
      month: 11,
      date: 25,
      name: "Christmas Day",
      sub: "National Holiday",
      tag: "PAID",
    },
  ];

  let upcoming: any[] = [];

  for (let year = currentYear; year <= currentYear + 1; year++) {
    for (const h of holidaysList) {
      // Create date object (months are 0-indexed)
      const hDate = new Date(year, h.month, h.date);
      if (hDate >= today) {
        upcoming.push({
          dateObj: hDate,
          name: h.name,
          sub: `${hDate.toLocaleDateString("en-US", { weekday: "long" })} • ${h.sub}`,
          tag: h.tag,
          date: `${hDate.toLocaleDateString("en-US", { month: "short" }).toUpperCase()} ${hDate.getDate()}`,
          displayFull: `${h.name} on ${hDate.toLocaleDateString("en-US", { month: "long", day: "numeric" })}`,
        });
      }
    }
  }

  upcoming.sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());
  return upcoming.slice(0, 2);
};

const holidays = getUpcomingHolidays();

const Attendance: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const attendance = useAppSelector((s) => s.data.attendance);
  const leaveRequests = useAppSelector((s) => s.data.leaveRequests);
  const employees = useAppSelector((s) => s.data.employees);
  const roleConfig = user
    ? ROLES_CONFIG[user.role.toLowerCase() as keyof typeof ROLES_CONFIG]
    : null;
  const canViewAll = roleConfig?.canViewAllAtt ?? false;

  const [now, setNow] = useState(new Date());
  const [status, setStatus] = useState<ClockStatus>("idle");
  const [workSec, setWorkSec] = useState(0);
  const [breakSec, setBreakSec] = useState(0);
  const [clockInTime, setClockInTime] = useState<Date | null>(null);
  const [clockOutTime, setClockOutTime] = useState<Date | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (status === "working")
      timerRef.current = setInterval(() => setWorkSec((s) => s + 1), 1000);
    else if (status === "break")
      timerRef.current = setInterval(() => setBreakSec((s) => s + 1), 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [status]);

  useEffect(() => {
    if (!user) return;
    const empProfile = employees.find(
      (e: any) => e.user === user?._id || e.user?._id === user?._id,
    );
    const actualEmpId = empProfile?._id || empProfile?.id;

    const today = new Date();
    const todayRecord = attendance.find((a) => {
      const rEmpId = a.empId || (a as any).employee;
      // Must exactly match the employee ID
      if (!rEmpId || !actualEmpId || String(rEmpId) !== String(actualEmpId))
        return false;

      const rDate = new Date(a.date);
      return (
        rDate.getDate() === today.getDate() &&
        rDate.getMonth() === today.getMonth() &&
        rDate.getFullYear() === today.getFullYear()
      );
    });

    if (todayRecord && (todayRecord as any).rawCheckIn && status === "idle") {
      setClockInTime(new Date((todayRecord as any).rawCheckIn));

      const totalBreakSec = (todayRecord as any).totalBreakSec || 0;

      if ((todayRecord as any).rawCheckOut) {
        setStatus("done");
        setClockOutTime(new Date((todayRecord as any).rawCheckOut));
        const diffMs =
          new Date((todayRecord as any).rawCheckOut).getTime() -
          new Date((todayRecord as any).rawCheckIn).getTime();
        setWorkSec(Math.max(0, Math.floor(diffMs / 1000) - totalBreakSec));
        setBreakSec(totalBreakSec);
      } else if ((todayRecord as any).breakStart) {
        setStatus("break");
        const diffMs =
          today.getTime() - new Date((todayRecord as any).rawCheckIn).getTime();
        setWorkSec(Math.max(0, Math.floor(diffMs / 1000) - totalBreakSec));

        const currentBreakMs =
          today.getTime() - new Date((todayRecord as any).breakStart).getTime();
        setBreakSec(totalBreakSec + Math.floor(currentBreakMs / 1000));
      } else {
        setStatus("working");
        const diffMs =
          today.getTime() - new Date((todayRecord as any).rawCheckIn).getTime();
        setWorkSec(Math.max(0, Math.floor(diffMs / 1000) - totalBreakSec));
        setBreakSec(totalBreakSec);
      }
    }
  }, [attendance, user, status]);

  const handleClockAction = async () => {
    const empProfile = employees.find(
      (e: any) => e.user === user?._id || e.user?._id === user?._id,
    );
    const targetId = empProfile?._id || empProfile?.id || user?._id;

    if (!targetId) {
      dispatch(
        showSnackbar({
          message: "Employee Profile not found",
          severity: "error",
        }),
      );
      return;
    }

    try {
      if (status === "idle") {
        const res = await dispatch(
          attendanceActionAsync({ employeeId: targetId, action: "check-in" }),
        ).unwrap();
        setStatus("working");
        setWorkSec(0);
        setBreakSec(0);
        setClockInTime(new Date(res.record.checkIn));
        dispatch(showSnackbar({ message: `Clocked in successfully` }));
      } else if (status === "working") {
        await dispatch(
          attendanceActionAsync({
            employeeId: targetId,
            action: "start-break",
          }),
        ).unwrap();
        setStatus("break");
        dispatch(showSnackbar({ message: "Break started", severity: "info" }));
      } else if (status === "break") {
        await dispatch(
          attendanceActionAsync({ employeeId: targetId, action: "end-break" }),
        ).unwrap();
        setStatus("working");
        dispatch(
          showSnackbar({ message: `Resumed · Break ended`, severity: "info" }),
        );
      }
    } catch (err: any) {
      dispatch(
        showSnackbar({
          message: err || "Failed to perform action",
          severity: "error",
        }),
      );
    }
  };

  const clockOut = async () => {
    const empProfile = employees.find(
      (e: any) => e.user === user?._id || e.user?._id === user?._id,
    );
    const targetId = empProfile?._id || empProfile?.id || user?._id;

    if (targetId) {
      try {
        const res = await dispatch(
          attendanceActionAsync({ employeeId: targetId, action: "check-out" }),
        ).unwrap();
        if (timerRef.current) clearInterval(timerRef.current);
        setClockOutTime(new Date(res.record.checkOut));
        setStatus("done");
        dispatch(showSnackbar({ message: `Clocked out successfully` }));
      } catch (err: any) {
        dispatch(
          showSnackbar({
            message: err || "Failed to clock out",
            severity: "error",
          }),
        );
      }
    } else {
      dispatch(
        showSnackbar({ message: "User ID not found", severity: "error" }),
      );
    }
  };

  const getGreetingText = () => {
    const firstName =
      user?.name?.split(" ")[0] || user?.email?.split("@")[0] || "there";
    if (status === "idle")
      return `Good morning, ${firstName}. You haven't started your shift yet.`;
    if (status === "working")
      return `You are currently clocked in, ${firstName}. Keep up the great work!`;
    if (status === "break") return `You are on break. Relax and recharge!`;
    return `Your shift has ended for today. Have a great evening!`;
  };

  const getSubGreetingText = () => {
    if (status === "idle") return "Ready to get things moving?";
    if (status === "working") return "Don't forget to take short breaks.";
    if (status === "break") return "Take your time.";
    return "See you tomorrow.";
  };

  const todayStr = new Date().toISOString().split("T")[0];
  const myLeaves = leaveRequests.filter((l) => {
    const lEmpId = l.empId || (l as any).employee?._id || (l as any).employee;
    const empProfile = employees.find(
      (e: any) => e.user === user?._id || e.user?._id === user?._id,
    );
    const actualEmpId = empProfile?._id || empProfile?.id;
    return (
      lEmpId &&
      actualEmpId &&
      String(lEmpId) === String(actualEmpId) &&
      l.status === "Approved"
    );
  });
  const annualLeavesTaken = myLeaves
    .filter((l) => l.type === "Annual")
    .reduce((acc, curr) => acc + (curr.days || 0), 0);
  const sickLeavesTaken = myLeaves
    .filter((l) => l.type === "Sick")
    .reduce((acc, curr) => acc + (curr.days || 0), 0);

  const totalEmployees = employees.length || 240; // Fallback to 240 if not fetched
  const presentToday = attendance.filter(
    (a) => a.date === todayStr && a.status === "Present",
  ).length;
  const onLeaveToday = attendance.filter(
    (a) => a.date === todayStr && a.status === "On Leave",
  ).length;
  const wfhToday = attendance.filter(
    (a) => a.date === todayStr && a.status === "WFH",
  ).length;
  const absentToday = totalEmployees - presentToday - onLeaveToday - wfhToday;

  const dynamicAttStats = [
    {
      label: "Present Today",
      value: presentToday.toString(),
      icon: "CheckCircle",
      delta: "",
      deltaType: "up" as const,
      color: "#16a34a",
      iconBg: "#dcfce7",
    },
    {
      label: "Absent",
      value: absentToday.toString(),
      icon: "Cancel",
      delta: "",
      deltaType: "down" as const,
      color: "#dc2626",
      iconBg: "#fee2e2",
    },
    {
      label: "On Leave",
      value: onLeaveToday.toString(),
      icon: "EventAvailable",
      delta: "",
      deltaType: "neutral" as const,
      color: "#d97706",
      iconBg: "#fef3c7",
    },
    {
      label: "WFH",
      value: wfhToday.toString(),
      icon: "Home",
      delta: "",
      deltaType: "neutral" as const,
      color: "#6BB8C3",
      iconBg: "#C9FDF2",
    },
  ];

  const renderBannerButton = () => {
    if (status === "idle") {
      return (
        <Button
          variant="contained"
          onClick={handleClockAction}
          sx={{
            background: "#53dd85ff",
            color: "white",
            px: 3,
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            "&:hover": { background: "#15803d" },
          }}
          startIcon={<PlayArrow />}
        >
          Clock In Now
        </Button>
      );
    }
    if (status === "working") {
      return (
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleClockAction}
            sx={{
              background: "#d97706",
              color: "white",
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              "&:hover": { background: "#b45309" },
            }}
            startIcon={<FreeBreakfast />}
          >
            Start Break
          </Button>
          <Button
            variant="contained"
            onClick={clockOut}
            sx={{
              background: "#dc2626",
              color: "white",
              px: 3,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              "&:hover": { background: "#b91c1c" },
            }}
            startIcon={<Stop />}
          >
            Clock Out
          </Button>
        </Box>
      );
    }
    if (status === "break") {
      return (
        <Button
          variant="contained"
          onClick={handleClockAction}
          sx={{
            background: "#B6F2D1",
            color: "#0f172a",
            px: 3,
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            "&:hover": { background: "#A4E0BF" },
          }}
          startIcon={<PlayArrow />}
        >
          Resume Work
        </Button>
      );
    }
    if (status === "done") {
      return (
        <Button
          variant="contained"
          disabled
          sx={{
            background: "rgba(255,255,255,0.3)",
            color: "#0f172a",
            px: 3,
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            opacity: 0.7,
            "&.Mui-disabled": {
              background: "rgba(255,255,255,0.4)",
              color: "#334155",
            },
          }}
          startIcon={<CheckCircle />}
        >
          Shift Completed
        </Button>
      );
    }
    return null;
  };

  const activeWeeklyData = weeklyData.map((d) => ({
    ...d,
    color:
      d.name === "FRI"
        ? status === "working"
          ? "#16a34a"
          : "#B6F2D1"
        : d.color,
  }));

  return (
    <Box>
      <SEO title="Attendance" noindex />
      {/* Row 1: Banner & Schedule */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #85D1DB, #B6F2D1)",
              color: "#0f172a",
              borderRadius: 4,
              height: "100%",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <CardContent
              sx={{
                p: 4,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  opacity: 0.8,
                  mb: 1,
                }}
              >
                Current Time
              </Typography>
              <Typography
                sx={{
                  fontSize: 48,
                  fontWeight: 700,
                  fontFamily: "'JetBrains Mono',monospace",
                  lineHeight: 1,
                  mb: 2,
                }}
              >
                {now.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                })}
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 500, mb: 0.5 }}>
                {getGreetingText()}
              </Typography>
              <Typography sx={{ fontSize: 14, opacity: 0.8, mb: 4 }}>
                {getSubGreetingText()}
              </Typography>
              <Box sx={{ position: "relative", zIndex: 1 }}>
                {renderBannerButton()}
              </Box>
              <AccessTime
                sx={{
                  position: "absolute",
                  right: -20,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 240,
                  opacity: 0.1,
                  zIndex: 0,
                }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 4,
              height: "100%",
              border: "1px solid #e5e7eb",
              boxShadow: "none",
            }}
          >
            <CardContent
              sx={{
                p: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                    Shift Schedule
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5 }}>
                    Mon - Fri, 09:00 - 18:00
                  </Typography>
                </Box>
                <Box
                  sx={{
                    background: "#C9FDF2",
                    color: "#6BB8C3",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1.5,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  Remote
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 2,
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                  Policy
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  Standard 8h + 1h Break
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  py: 2,
                  mb: "auto",
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                  Overtime
                </Typography>
                <Typography
                  sx={{ fontSize: 13, fontWeight: 600, color: "#16a34a" }}
                >
                  Enabled
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  p: 2,
                  background: "#f8fafc",
                  borderRadius: 2,
                  alignItems: "center",
                  mt: 2,
                }}
              >
                <InfoOutlined sx={{ color: "#64748b", fontSize: 20 }} />
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                  Your next holiday is{" "}
                  <strong>
                    {holidays.length > 0
                      ? holidays[0].displayFull
                      : "coming up soon"}
                    .
                  </strong>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Row 2: Stats */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          {
            icon: <PlayArrow sx={{ fontSize: 16, color: "#6BB8C3" }} />,
            bg: "#C9FDF2",
            label: "SHIFT START",
            value: clockInTime ? fmtTime(clockInTime) : "--:--",
          },
          {
            icon: <AccessTime sx={{ fontSize: 16, color: "#16a34a" }} />,
            bg: "#dcfce7",
            label: "WORKED HOURS",
            value: fmtHM(workSec),
          },
          {
            icon: <FreeBreakfast sx={{ fontSize: 16, color: "#d97706" }} />,
            bg: "#fef3c7",
            label: "BREAK TIME",
            value: fmtHM(breakSec),
          },
          {
            icon: <AccessTime sx={{ fontSize: 16, color: "#64748b" }} />,
            bg: "#f1f5f9",
            label: "CURRENT STATUS",
            value:
              status === "working"
                ? "On Duty"
                : status === "break"
                  ? "On Break"
                  : "Off Duty",
          },
        ].map((item, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                boxShadow: "none",
              }}
            >
              <CardContent
                sx={{
                  p: "16px !important",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      background: item.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#94a3b8",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 700,
                    fontFamily: "'JetBrains Mono',monospace",
                    color: "#0f172a",
                  }}
                >
                  {item.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Row 3: Weekly Chart & Recent Activity */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              borderRadius: 4,
              height: "100%",
              border: "1px solid #e5e7eb",
              boxShadow: "none",
            }}
          >
            <CardContent
              sx={{
                p: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 4,
                }}
              >
                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                  Weekly Attendance
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#6BB8C3",
                      background: "#C9FDF2",
                      px: 2,
                      py: 0.5,
                      borderRadius: 99,
                    }}
                  >
                    This Week
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: "#64748b",
                      px: 2,
                      py: 0.5,
                      cursor: "pointer",
                    }}
                  >
                    Last Week
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flexGrow: 1, minHeight: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={activeWeeklyData}
                    margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                    barSize={32}
                  >
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "#94a3b8", fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                      dy={10}
                    />
                    <Tooltip
                      cursor={{ fill: "#f8fafc" }}
                      formatter={(v: any) => [`${v} hours`, "Worked"]}
                      contentStyle={{
                        borderRadius: 8,
                        border: "none",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="value" radius={[6, 6, 6, 6]}>
                      {activeWeeklyData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card
            sx={{
              borderRadius: 4,
              height: "100%",
              border: "1px solid #e5e7eb",
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 3 }}>
                Recent Activity
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  pl: 2,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 4,
                    top: 8,
                    bottom: 8,
                    width: 2,
                    background: "#e2e8f0",
                  },
                }}
              >
                {recentActivity.map((act, i) => (
                  <Box key={i} sx={{ position: "relative", mb: 3, pl: 3 }}>
                    <Box
                      sx={{
                        position: "absolute",
                        left: -7,
                        top: 4,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: act.color,
                        border: "2px solid white",
                        boxShadow: "0 0 0 1px #e2e8f0",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#64748b",
                        mb: 0.5,
                      }}
                    >
                      {act.time}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13, fontWeight: 500, color: "#334155" }}
                    >
                      {act.title}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#85D1DB",
                    cursor: "pointer",
                  }}
                >
                  View Full Log
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Row 4: Holidays & Leave Summary */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 4,
              height: "100%",
              border: "1px solid #e5e7eb",
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
              >
                <WbSunny sx={{ color: "#d97706", fontSize: 20 }} />
                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                  Upcoming Holidays
                </Typography>
              </Box>
              {holidays.map((hol, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    gap: 2,
                    p: 2,
                    background: "#f8fafc",
                    borderRadius: 2,
                    mb: i === 0 ? 1.5 : 0,
                    border: "1px solid #f1f5f9",
                  }}
                >
                  <Box sx={{ textAlign: "center", minWidth: 40 }}>
                    <Typography
                      sx={{ fontSize: 10, fontWeight: 700, color: "#ef4444" }}
                    >
                      {hol.date.split(" ")[0]}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}
                    >
                      {hol.date.split(" ")[1]}
                    </Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                      {hol.name}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 11, color: "#64748b", mt: 0.5 }}
                    >
                      {hol.sub}
                    </Typography>
                  </Box>
                  {hol.tag && (
                    <Box
                      sx={{
                        background: "#dcfce7",
                        color: "#16a34a",
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: 10,
                        fontWeight: 700,
                        height: "fit-content",
                      }}
                    >
                      {hol.tag}
                    </Box>
                  )}
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 4,
              height: "100%",
              border: "1px solid #e5e7eb",
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <CalendarToday sx={{ color: "#85D1DB", fontSize: 20 }} />
                  <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                    Leave Summary
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#85D1DB",
                    cursor: "pointer",
                  }}
                >
                  + Request Leave
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 2,
                      background: "#f8fafc",
                      borderRadius: 2,
                      border: "1px solid #f1f5f9",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#64748b",
                        letterSpacing: "0.05em",
                        mb: 1,
                      }}
                    >
                      ANNUAL LEAVE
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 28,
                          fontWeight: 700,
                          fontFamily: "'JetBrains Mono',monospace",
                          color: "#0f172a",
                          lineHeight: 1,
                        }}
                      >
                        {annualLeavesTaken}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                        / 25 days
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(annualLeavesTaken / 25) * 100}
                      sx={{
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: "#e2e8f0",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#85D1DB",
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 2,
                      background: "#f8fafc",
                      borderRadius: 2,
                      border: "1px solid #f1f5f9",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#64748b",
                        letterSpacing: "0.05em",
                        mb: 1,
                      }}
                    >
                      SICK LEAVE
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 28,
                          fontWeight: 700,
                          fontFamily: "'JetBrains Mono',monospace",
                          color: "#0f172a",
                          lineHeight: 1,
                        }}
                      >
                        {sickLeavesTaken}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                        / 10 days
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(sickLeavesTaken / 10) * 100}
                      sx={{
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: "#e2e8f0",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#16a34a",
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Typography
                sx={{
                  fontSize: 11,
                  color: "#94a3b8",
                  textAlign: "center",
                  mt: 3,
                  fontStyle: "italic",
                }}
              >
                No pending leave requests at this time.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Admin Log (Only visible if canViewAll) */}
      {canViewAll && (
        <>
          <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 2 }}>
            Admin Attendance Tools
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            {dynamicAttStats.map((s) => (
              <Grid item xs={12} sm={6} lg={3} key={s.label}>
                <StatCard {...s} />
              </Grid>
            ))}
          </Grid>
          <Card
            sx={{
              borderRadius: 4,
              border: "1px solid #e5e7eb",
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: "16px 24px",
                borderBottom: "1px solid #e5e7eb",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                All Employees Attendance Log
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download sx={{ fontSize: 16 }} />}
                sx={{
                  fontSize: 12,
                  borderRadius: 1.5,
                  borderColor: "#e5e7eb",
                  color: "#334155",
                  "&:hover": { background: "#f8fafc", borderColor: "#cbd5e1" },
                }}
              >
                Export CSV
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "Employee",
                      "Date",
                      "Clock In",
                      "Clock Out",
                      "Work Hrs",
                      "Break",
                      "Status",
                    ].map((h) => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendance.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Avatar
                            initials={r.initials}
                            color={r.avColor}
                            size={32}
                          />
                          <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                              {r.name}
                            </Typography>
                            {r.sysId && (
                              <Typography
                                sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}
                              >
                                {r.sysId}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                          fontSize: 12,
                          color: "#64748b",
                          fontFamily: "'JetBrains Mono',monospace",
                        }}
                      >
                        {r.date}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                          fontSize: 13,
                          fontFamily: "'JetBrains Mono',monospace",
                          fontWeight: 500,
                        }}
                      >
                        {r.clockIn}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                          fontSize: 13,
                          fontFamily: "'JetBrains Mono',monospace",
                          fontWeight: 500,
                        }}
                      >
                        {r.clockOut}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                          fontSize: 13,
                          fontFamily: "'JetBrains Mono',monospace",
                          fontWeight: 700,
                          color: "#0f172a",
                        }}
                      >
                        {r.workHrs}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                          fontSize: 12,
                          color: "#64748b",
                          fontFamily: "'JetBrains Mono',monospace",
                        }}
                      >
                        {r.breakHrs}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1.5,
                          px: 2,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        <StatusBadge status={r.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </>
      )}
    </Box>
  );
};

export default Attendance;
